import { takeLatest, call, put, select, all } from '@redux-saga/core/effects';
import Logger from '@noths/logger/build/client';
import Types from '../../types';
import { register, signIn } from '../../utils/user';
import { getQueryParams } from '../../utils/selectors';
import { getRedirectPath, isInCheckoutFlow } from '../../utils/redirect';
import { scrollToTopOfPage } from '../../utils/page';
import { authenticateUserWithNoths } from '../../../services/AuthenticateService';
import { createPreferences } from '../MarketingPreferences';
import { privacyPolicyAccept } from '../../utils/appsync';

import {
  trackRegisterFormSubmitClick,
  trackRegisterSucceeded,
  trackMarketingPreferencesRegistration,
  trackRegisterEmailAlreadyExistsFailure,
} from '../../../analytics';
import { persistError } from '../../utils/errors';
import getErrorMessage from '../../utils/getErrorMessage';

export function* signInSuccess(email, marketingEmailsChecked) {
  try {
    yield call(authenticateUserWithNoths);
    const queryParams = yield select(getQueryParams);
    yield call(trackRegisterSucceeded, email, marketingEmailsChecked);

    window.location = `//${window.location.host}${getRedirectPath(queryParams)}`;
  } catch (error) {
    Logger.error(getErrorMessage(error), { extra: { description: `Register Sign In Session` } });
    persistError('signIn', Types.REGISTER_SESSION_AUTH_ERROR_DURING_SIGN_IN);
    yield put({ type: Types.SIGN_OUT });
  }
}

export function* registerFailure(error) {
  if (error.message && error.message.includes('[UsernameExistsError]')) {
    yield call(trackRegisterEmailAlreadyExistsFailure);
    yield put({ type: Types.REGISTER_EMAIL_ALREADY_TAKEN });
  } else if (error.code === 'GuestUserRegistrationError') {
    yield put({ type: Types.REGISTER_EMAIL_ALREADY_TAKEN });
  } else if (error.code === 'NotAuthorizedError') {
    yield put({ type: Types.REGISTER_PASSWORD_ERROR });
  } else {
    yield put({ type: Types.REGISTER_COGNITO_ERROR });
  }
  yield call(scrollToTopOfPage);
}

export function* registerSubmit({
  firstName,
  lastName,
  email,
  password,
  marketingEmailsChecked,
  marketingMailChecked,
  recaptchaChecked,
  requiresRecaptcha,
}) {
  try {
    const queryParams = yield select(getQueryParams);
    const inCheckout = isInCheckoutFlow(queryParams);
    yield call(trackRegisterFormSubmitClick);
    const userAttributes = {
      name: firstName,
      family_name: lastName,
      email,
    };
    const preferences = {
      email: { value: email, currentStatus: marketingEmailsChecked },
      directMail: {
        value: email,
        currentStatus: marketingMailChecked,
      },
      source: inCheckout ? 'CHECKOUT' : 'REGISTER',
    };

    if (requiresRecaptcha && !recaptchaChecked) {
      yield put({ type: Types.RECAPTCHA_ERROR });
      yield call(scrollToTopOfPage);
    } else {
      yield call(register, password, userAttributes);
      yield call(signIn, email, password);
      yield all([
        call(createPreferences, preferences),
        call(logPrivacyPolicyViewed),
        call(trackMarketingPreferencesRegistration, marketingEmailsChecked, marketingMailChecked),
      ]);
      yield call(signInSuccess, email, marketingEmailsChecked);
    }
  } catch (error) {
    Logger.error(getErrorMessage(error), { extra: { description: `Register Submit` } });
    yield call(registerFailure, error);
  }
}

export function* logPrivacyPolicyViewed() {
  try {
    yield privacyPolicyAccept();
  } catch (error) {
    Logger.error(getErrorMessage(error), { extra: { description: `Setting Privacy Policy Read` } });
  }
}

function* registerSaga() {
  yield takeLatest(Types.REGISTER_SUBMIT, registerSubmit);
}

export default registerSaga;
